import React from 'react';
import { useTranslation } from 'react-i18next';
import { Snowflake } from 'lucide-react';
import { CarrierPreviewItem } from '@/types';
import { formatTimeInIraqTimezone, normalizeToTranslationKey } from '@/utils';

interface SmartCarrierMatchItemProps {
  carrier: CarrierPreviewItem;
}

const SmartCarrierMatchItem: React.FC<SmartCarrierMatchItemProps> = ({ carrier }) => {
  const { t } = useTranslation();

  return (
    <div className='flex items-center gap-3 rounded-xl border border-[rgb(232,230,227)] bg-white px-3 py-2.5'>
      {/* Carrier avatar + name */}
      <div className='flex items-center gap-2.5 min-w-0 flex-[3]'>
        <div className='w-9 h-9 rounded-lg border border-[rgb(232,230,227)] overflow-hidden shrink-0 bg-[rgb(245,245,243)]'>
          {carrier.carrier.avatar?.abs_url ? (
            <img
              src={carrier.carrier.avatar.abs_url}
              alt={carrier.carrier.title}
              className='w-full h-full object-cover'
            />
          ) : (
            <div className='w-full h-full flex items-center justify-center text-[10px] font-bold text-[rgb(115,123,140)]'>
              {carrier.carrier.title?.charAt(0) ?? '?'}
            </div>
          )}
        </div>
        <span className='text-[13px] font-medium text-[rgb(57,57,60)] truncate'>
          {String(t(normalizeToTranslationKey(carrier.carrier.title), {
            defaultValue: carrier.carrier.title,
          }))}
        </span>
        {/* TEMP-9 — cold-chain capability. Renders ONLY when the backend
            explicitly says true; absent/undefined shows nothing. */}
        {carrier.supports_temperature_control === true && (
          <span
            data-testid='carrier-temperature-icon'
            title={String(t('temperature_controlled'))}
            aria-label={String(t('temperature_controlled'))}
            className='inline-flex shrink-0'
          >
            <Snowflake className='h-3.5 w-3.5 text-blue-500' strokeWidth={2} />
          </span>
        )}
      </div>

      {/* Ship Before */}
      <div className='flex flex-col min-w-0 flex-[2] text-start'>
        <span className='text-[10px] text-[rgb(115,123,140)]'>
          {String(t('ship_before'))}
        </span>
        <span className='text-[13px] font-medium text-[rgb(57,57,60)]'>
          {formatTimeInIraqTimezone(carrier.if_ship_before)}
        </span>
      </div>

      {/* Service Type */}
      <div className='flex flex-col min-w-0 flex-[2] text-start'>
        <span className='text-[10px] text-[rgb(115,123,140)]'>
          {String(t('service'))}
        </span>
        <span className='text-[13px] font-medium text-[rgb(57,57,60)]'>
          {carrier.serviceType ? String(t(carrier.serviceType)) : carrier.average_delivery_time}
        </span>
      </div>
    </div>
  );
};

export default SmartCarrierMatchItem;
